import {BiomatchResponse} from '@chekin/api';
import {
  IMAGE_SERVICE_ORIGIN,
  IV_DOCUMENT_IMAGE_TYPES,
  OCRDetectedData,
} from '@chekin/core';
import {ANALYTIC_EVENTS} from '../../../analytics';
import {OCRFailureResolution, StoredImageDetails} from './types';

function buildOCRImageDetails(
  image: string,
  docType: IV_DOCUMENT_IMAGE_TYPES,
  data?: OCRDetectedData,
): StoredImageDetails {
  return {
    image,
    type: docType,
    mrzDetected: data?.mrz_detected,
    barcodeDetected: data?.barcode_detected,
    id: data?.id,
    service: IMAGE_SERVICE_ORIGIN.ocr,
    uploaded: true,
  };
}

function buildBiomatchImageDetails(
  image: string,
  docType: IV_DOCUMENT_IMAGE_TYPES,
  data?: BiomatchResponse,
): StoredImageDetails {
  return {
    image,
    type: docType,
    faceDetected: data?.is_face_detected,
    id: data?.id,
    service: IMAGE_SERVICE_ORIGIN.biomatch,
    uploaded: true,
  };
}

function getBiomatchDocumentPictureId(front?: {id?: string; service?: string}) {
  return front?.service === IMAGE_SERVICE_ORIGIN.biomatch && front.id ? front.id : null;
}

function omitRawImage(details: StoredImageDetails) {
  const {image: _, ...detailsWithoutRawImage} = details;
  return detailsWithoutRawImage;
}

function shouldPersistOCRImage(args: {
  isBiomatchAndPassport: boolean;
  onlyScanning?: boolean;
  docType?: IV_DOCUMENT_IMAGE_TYPES;
}) {
  return Boolean(!args.isBiomatchAndPassport && !args.onlyScanning && args.docType);
}

function resolveOCRFailure(args: {
  documentTypeWithoutMrz?: boolean;
  documentTypeBothSidesMrz?: boolean;
  hasFrontSideImage: boolean;
  docType?: IV_DOCUMENT_IMAGE_TYPES;
}): OCRFailureResolution {
  if (args.documentTypeWithoutMrz) {
    return {type: 'success_without_mrz'};
  }

  if (
    args.documentTypeBothSidesMrz &&
    args.hasFrontSideImage &&
    args.docType === IV_DOCUMENT_IMAGE_TYPES.back
  ) {
    return {type: 'retry_front_side_as_ocr'};
  }

  return {type: 'fail'};
}

function shouldRunOCRAfterBiomatch(args: {
  isBiomatchAndPassport: boolean;
  documentTypeWithoutMrz?: boolean;
}) {
  return args.isBiomatchAndPassport && !args.documentTypeWithoutMrz;
}

function getDocumentCompletionEvent(docType: IV_DOCUMENT_IMAGE_TYPES) {
  if (docType === IV_DOCUMENT_IMAGE_TYPES.front) {
    return ANALYTIC_EVENTS.ivFinishingFrontSideDocument;
  }

  if (docType === IV_DOCUMENT_IMAGE_TYPES.back) {
    return ANALYTIC_EVENTS.ivFinishingBackSideDocument;
  }

  return null;
}

export {
  buildBiomatchImageDetails,
  buildOCRImageDetails,
  getBiomatchDocumentPictureId,
  getDocumentCompletionEvent,
  omitRawImage,
  resolveOCRFailure,
  shouldPersistOCRImage,
  shouldRunOCRAfterBiomatch,
};
